import { Link, createFileRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/lib/auth-context'
import { SetupStage2Checklist } from '@/components/setup-stage2-checklist'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { API_BASE } from '@/lib/api-base'
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { formatBRDate } from '@/lib/br-dates'
import {
  averageIepScore,
  CHART_EXPECTED_COLOR,
  CHART_REAL_COLOR,
  IEP_STATUS_STYLES,
  parseLoteIdFromIntelUrl,
  scoreToIepStatus,
  type IepStatus,
} from '@/lib/iep-status'

interface CockpitData {
  data_referencia: string
  operacao_hoje: {
    coletado_hoje: number
    coletado_hoje_vs_media_7d: number
    quebrados_hoje: number
    percentual_quebrados: number
    pendentes_classificacao: number
    pendentes_velhos_24h: number
    tempo_medio_classificacao_horas: number | null
    mortalidade_hoje_pct?: number | null
  }
  alertas: {
    criticos_ativos: number
    warnings_ativos: number
    total_ativos: number
    top_alertas: {
      id: string
      tipo: string
      severidade: string
      mensagem: string
      idade_horas: number
      acao_sugerida: string | null
      acao_url: string | null
    }[]
  }
  estoque_tipos: {
    total_classificado: number
    total_coletado: number
    total_nao_classificado: number
    percentual_classificado: number
    tipos: { tipo: string; quantidade: number; percentual: number }[]
  }
  financeiro: {
    recebiveis_pendentes: number
    recebiveis_atrasados: number
    vence_hoje: number
    vence_em_3dias: number
    caixa_realizado_hoje: number
    caixa_previsto_7d: number
  }
}

interface HeatmapGalpao {
  id: string
  nome: string
  status: string
  lote_id: string | null
  lote_codigo: string | null
  iep_score: number | null
  iep_status: IepStatus
}

interface HeatmapResponse {
  galpoes: HeatmapGalpao[]
}

interface DashboardLegacyResponse {
  ovos_hoje: number
  ovos_ontem: number
  producao_ave_dia: number
  estoque_teorico: number
  tendencia_7dias: { data: string; total: number }[]
}

interface IntelLoteResponse {
  lote: {
    id: string
    codigo: string
    linhagem: string
    data_alojamento: string
    quantidade_inicial: number
    quantidade_atual: number
  }
  curva: { linhagem: string; versao: string } | null
  series: {
    semanas: {
      semana: number
      data_inicio: string
      data_fim: string
      real: {
        postura_pct: number
        mortalidade_pct: number
        consumo_g_ave_dia: number
        fcr_estimado: number | null
      }
      esperado: { postura_pct: number; mortalidade_pct: number; consumo_g_ave_dia: number }
    }[]
  }
  metricas_periodo: {
    postura_pct: number
    mortalidade_pct: number
    consumo_g_ave_dia: number
    comercializaveis_pct: number
    fcr_estimado_periodo: number | null
  }
  iep: {
    score: number
    status: Exclude<IepStatus, 'sem_dados'>
    explain: string[]
  }
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

function formatNumber(value: number) {
  return new Intl.NumberFormat('pt-BR').format(value)
}

function formatScore(value: number) {
  return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(value)
}

function formatFcrEstimado(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

function DashboardPage() {
  const { user, apiFetch, isSuperAdmin, selectedTenantId } = useAuth()
  const [data, setData] = useState<CockpitData | null>(null)
  const [heatmap, setHeatmap] = useState<HeatmapResponse | null>(null)
  const [dashboardLegacy, setDashboardLegacy] = useState<DashboardLegacyResponse | null>(null)
  const [loteDialogOpen, setLoteDialogOpen] = useState(false)
  const [loteDetail, setLoteDetail] = useState<IntelLoteResponse | null>(null)
  const [loteLoading, setLoteLoading] = useState(false)
  const [activeLoteCodigo, setActiveLoteCodigo] = useState<string | null>(null)

  const apiBase = API_BASE
  const fetchAll = useCallback(async () => {
    try {
      const [cockpitRes, heatmapRes, legacyRes] = await Promise.all([
        apiFetch(`${apiBase}/api/dashboard/cockpit`),
        apiFetch(`${apiBase}/api/intel/heatmap`),
        apiFetch(`${apiBase}/api/dashboard/`),
      ])
      if (cockpitRes.ok) setData(await cockpitRes.json())
      if (heatmapRes.ok) setHeatmap(await heatmapRes.json())
      if (legacyRes.ok) setDashboardLegacy(await legacyRes.json())
    } catch (e) {
      console.error(e)
    }
  }, [apiFetch, apiBase])

  useEffect(() => {
    if (!user) return
    if (isSuperAdmin && !selectedTenantId) return
    void fetchAll()
  }, [user, isSuperAdmin, selectedTenantId, fetchAll])

  const openLoteDialog = useCallback(
    async (loteId: string, loteCodigo?: string | null) => {
      setLoteDialogOpen(true)
      setLoteLoading(true)
      setLoteDetail(null)
      setActiveLoteCodigo(loteCodigo ?? null)
      try {
        const res = await apiFetch(`${apiBase}/api/intel/lote/${loteId}`)
        if (res.ok) {
          const detail = (await res.json()) as IntelLoteResponse
          setLoteDetail(detail)
          setActiveLoteCodigo(detail.lote.codigo)
        }
      } catch (e) {
        console.error(e)
      } finally {
        setLoteLoading(false)
      }
    },
    [apiBase, apiFetch],
  )

  const tendenciaChartData = useMemo(() => {
    if (!dashboardLegacy?.tendencia_7dias?.length) return []
    return dashboardLegacy.tendencia_7dias.map((d) => ({
      data: d.data,
      label: formatBRDate(d.data),
      total: d.total,
    }))
  }, [dashboardLegacy])

  const donutData = useMemo(() => {
    if (!data) return []
    const { criticos_ativos, warnings_ativos } = data.alertas
    return [
      { name: 'Críticos', value: criticos_ativos, color: IEP_STATUS_STYLES.vermelho.hex },
      { name: 'Avisos', value: warnings_ativos, color: IEP_STATUS_STYLES.amarelo.hex },
    ].filter((d) => d.value > 0)
  }, [data])

  const posturaChartData = useMemo(() => {
    if (!loteDetail?.series?.semanas?.length) return []
    return loteDetail.series.semanas.map((s) => ({
      semana: s.semana,
      real: s.real.postura_pct,
      esperado: s.esperado.postura_pct,
    }))
  }, [loteDetail])

  const iepMedio = useMemo(
    () => (heatmap ? averageIepScore(heatmap.galpoes) : null),
    [heatmap],
  )

  const iepMedioStatus = iepMedio != null ? scoreToIepStatus(iepMedio) : null

  const showSkeleton = isSuperAdmin && !selectedTenantId

  if (showSkeleton) {
    return (
      <div className="py-8 px-8 bg-gray-50/50 min-h-full">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Cockpit</h1>
          <p className="mt-1 text-sm text-gray-500">Visão geral da operação e performance.</p>
        </header>
        <p className="text-muted-foreground">Selecione uma granja no menu lateral.</p>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="py-8 px-8 bg-gray-50/50 min-h-full flex items-center justify-center">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    )
  }

  const op = data.operacao_hoje
  const alertas = data.alertas
  const estoque = data.estoque_tipos
  const fin = data.financeiro

  const totalEstoque = estoque.total_classificado + estoque.total_nao_classificado || 1
  const pctClassificado = totalEstoque ? (estoque.total_classificado / totalEstoque) * 100 : 0
  const pctNaoClassificado = totalEstoque ? (estoque.total_nao_classificado / totalEstoque) * 100 : 0

  return (
    <div className="py-8 px-8 bg-gray-50/50 min-h-full overflow-y-auto">
      <header className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Cockpit</h1>
        <p className="mt-1 text-sm text-gray-500">Visão geral da operação e performance.</p>
      </header>

      <SetupStage2Checklist />

      <section className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Alertas ativos</h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex items-center justify-between lg:col-span-1">
            <div className="h-24 w-24 relative flex-shrink-0">
              {donutData.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={28}
                        outerRadius={40}
                        paddingAngle={0}
                        dataKey="value"
                      >
                        {donutData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-xl font-bold text-gray-800">{alertas.total_ativos}</span>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center rounded-full bg-gray-100">
                  <span className="text-xl font-bold text-gray-500">0</span>
                </div>
              )}
            </div>
            <div className="flex flex-col ml-4 min-w-0">
              <div className="space-y-2 mt-2">
                <div className="flex items-center text-sm">
                  <span className={`h-2 w-2 rounded-full mr-2 flex-shrink-0 ${IEP_STATUS_STYLES.vermelho.bgClass}`} />
                  <span className="text-gray-600">{alertas.criticos_ativos} Críticos</span>
                </div>
                <div className="flex items-center text-sm">
                  <span className={`h-2 w-2 rounded-full mr-2 flex-shrink-0 ${IEP_STATUS_STYLES.amarelo.bgClass}`} />
                  <span className="text-gray-600">{alertas.warnings_ativos} Avisos</span>
                </div>
              </div>
            </div>
          </div>

          {alertas.top_alertas.length > 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
              <div className="space-y-2">
                {alertas.top_alertas.slice(0, 5).map((alert) => {
                  const loteId = parseLoteIdFromIntelUrl(alert.acao_url)
                  const severityStyle =
                    alert.severidade === 'critical' ? IEP_STATUS_STYLES.vermelho : IEP_STATUS_STYLES.amarelo
                  return (
                    <div key={alert.id} className="flex items-start justify-between p-2 rounded bg-muted/50">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${severityStyle.bgClass} ${severityStyle.textClass}`}
                          >
                            {alert.severidade}
                          </span>
                          <span className="text-sm font-medium">{alert.tipo}</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{alert.mensagem}</p>
                      </div>
                      {loteId ? (
                        <Button
                          type="button"
                          variant="link"
                          className="ml-2 flex-shrink-0 h-auto p-0"
                          onClick={() => void openLoteDialog(loteId)}
                        >
                          {alert.acao_sugerida || 'Ver lote'}
                        </Button>
                      ) : alert.acao_url?.startsWith('http') ? (
                        <a
                          href={alert.acao_url}
                          className="ml-2 text-sm font-medium text-primary hover:underline flex-shrink-0"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {alert.acao_sugerida || 'Ver'}
                        </a>
                      ) : null}
                    </div>
                  )
                })}
              </div>
              <Link to="/alertas" className="mt-4 inline-block text-sm font-medium text-[#2a78d6] hover:underline">
                Ver todos os alertas →
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2 flex items-center">
              <p className="text-sm text-muted-foreground">Nenhum alerta ativo no momento.</p>
            </div>
          )}
        </div>
      </section>

      <section className="mb-8 space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 flex flex-col items-center justify-center text-center">
          {iepMedio != null && iepMedioStatus ? (
            <>
              <p className="text-sm font-medium text-gray-500 mb-2">Saúde geral da granja (IEP médio)</p>
              <div
                className={`inline-flex items-center justify-center rounded-full w-36 h-36 border-8 ${IEP_STATUS_STYLES[iepMedioStatus].bgClass} ${IEP_STATUS_STYLES[iepMedioStatus].textClass}`}
                style={{ borderColor: IEP_STATUS_STYLES[iepMedioStatus].hex }}
              >
                <span className="text-4xl font-bold">{formatScore(iepMedio)}</span>
              </div>
              <p className="mt-3 text-sm capitalize text-muted-foreground">Status: {iepMedioStatus.replace('_', ' ')}</p>
            </>
          ) : (
            <p className="text-muted-foreground">Sem dados de IEP ainda</p>
          )}
        </div>

        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Heatmap de galpões</h3>
          {!heatmap?.galpoes?.length ? (
            <p className="text-sm text-muted-foreground">Nenhum galpão encontrado.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {heatmap.galpoes.map((g) => {
                const styles = IEP_STATUS_STYLES[g.iep_status]
                const clickable = !!g.lote_id
                return (
                  <button
                    key={g.id}
                    type="button"
                    disabled={!clickable}
                    onClick={() => g.lote_id && void openLoteDialog(g.lote_id, g.lote_codigo)}
                    className={`rounded-lg p-4 text-left min-h-[100px] transition-opacity ${styles.bgClass} ${styles.textClass} ${
                      clickable ? 'hover:opacity-90 cursor-pointer' : 'opacity-80 cursor-default'
                    }`}
                  >
                    <p className="font-semibold text-sm leading-tight">{g.nome}</p>
                    <p className="text-xs mt-1 opacity-90">
                      {g.lote_codigo ? `Plantel ${g.lote_codigo}` : 'Sem plantel ativo'}
                    </p>
                    {g.iep_score != null && (
                      <p className="text-lg font-bold mt-2">IEP {formatScore(g.iep_score)}</p>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <section className="mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-md">
          <h3 className="text-sm font-medium text-gray-500">Pendentes Classificação</h3>
          <div className="mt-2 flex items-baseline">
            <span className="text-3xl font-bold text-gray-900">{op.pendentes_classificacao}</span>
            <span className="ml-2 text-sm font-medium text-gray-500">lotes</span>
          </div>
          {op.pendentes_velhos_24h > 0 && (
            <div className="mt-6">
              <span
                className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${IEP_STATUS_STYLES.vermelho.bgClass} ${IEP_STATUS_STYLES.vermelho.textClass}`}
              >
                +{op.pendentes_velhos_24h} velhos ({`>`}24h)
              </span>
            </div>
          )}
          {typeof op.mortalidade_hoje_pct === 'number' && (
            <p className="mt-4 text-sm text-muted-foreground">
              Mortalidade hoje: {formatScore(op.mortalidade_hoje_pct)}%
            </p>
          )}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-sm font-medium text-gray-500">Coletado Hoje</h3>
          <div className="mt-2 flex items-baseline">
            <span className="text-3xl font-bold text-[#2a78d6]">{formatNumber(op.coletado_hoje)}</span>
            <span className="ml-2 text-sm font-medium text-gray-500">unidades</span>
          </div>
          <div className="mt-1 flex items-center text-sm">
            <span className={op.coletado_hoje_vs_media_7d >= 0 ? 'text-[#0ca30c] font-medium' : 'text-[#d03b3b] font-medium'}>
              {op.coletado_hoje_vs_media_7d >= 0 ? '+' : ''}
              {op.coletado_hoje_vs_media_7d.toFixed(1).replace('.', ',')}%
            </span>
            <span className="text-gray-400 ml-1">vs média 7d</span>
          </div>
          <div className="mt-4 h-20 w-full">
            {tendenciaChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tendenciaChartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <Tooltip
                    formatter={(value: number) => [formatNumber(value), 'Coletado']}
                    labelFormatter={(_, payload) =>
                      payload?.[0]?.payload?.label ? String(payload[0].payload.label) : ''
                    }
                  />
                  <Line
                    type="monotone"
                    dataKey="total"
                    stroke={CHART_REAL_COLOR}
                    strokeWidth={2}
                    dot={{ r: 3, strokeWidth: 2, fill: CHART_REAL_COLOR }}
                    activeDot={{ r: 4 }}
                  />
                  <XAxis dataKey="label" hide />
                  <YAxis hide domain={['auto', 'auto']} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-xs text-muted-foreground">Sem histórico de 7 dias.</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Visão Geral do Estoque</h3>
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Ovos Classificados</span>
                <span className="font-semibold text-[#0ca30c]">{formatNumber(estoque.total_classificado)}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${IEP_STATUS_STYLES.verde.bgClass}`}
                  style={{ width: `${Math.min(pctClassificado, 100)}%` }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Ovos N. Classificados</span>
                <span className="font-semibold text-gray-700">{formatNumber(estoque.total_nao_classificado)}</span>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-2.5">
                <div
                  className={`h-2.5 rounded-full transition-all ${IEP_STATUS_STYLES.sem_dados.bgClass}`}
                  style={{ width: `${Math.min(pctNaoClassificado, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <section className="mt-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Resumo Financeiro</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-100 flex flex-col justify-between h-32">
            <p className="text-xs font-bold uppercase tracking-wide text-[#fab219]">Receita Pendente</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(fin.recebiveis_pendentes)}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 flex flex-col justify-between h-32">
            <p className="text-xs font-bold uppercase tracking-wide text-[#d03b3b]">Atrasados</p>
            <div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(fin.recebiveis_atrasados)}</p>
              <p className="text-xs font-medium mt-1 text-[#d03b3b]">Faturas vencidas</p>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 flex flex-col justify-between h-32">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Vence Hoje</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(fin.vence_hoje)}</p>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100 flex flex-col justify-between h-32">
            <p className="text-xs font-bold uppercase tracking-wide text-[#0ca30c]">Caixa Hoje (Disponível)</p>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(fin.caixa_realizado_hoje)}</p>
          </div>
        </div>
      </section>

      <Dialog
        open={loteDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setLoteDialogOpen(false)
            setLoteDetail(null)
            setActiveLoteCodigo(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto gap-0 p-0">
          <DialogTitle className="sr-only">
            {activeLoteCodigo ? `Detalhe do plantel ${activeLoteCodigo}` : 'Detalhe do plantel'}
          </DialogTitle>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold pr-10">
              {loteDetail
                ? `Plantel ${loteDetail.lote.codigo}`
                : activeLoteCodigo
                  ? `Plantel ${activeLoteCodigo}`
                  : 'Plantel'}
            </h2>
            {loteLoading && <p className="text-sm text-muted-foreground">Carregando...</p>}
            {!loteLoading && loteDetail && (
              <>
                <div className="flex flex-wrap items-center gap-3">
                  <span
                    className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${IEP_STATUS_STYLES[loteDetail.iep.status].bgClass} ${IEP_STATUS_STYLES[loteDetail.iep.status].textClass}`}
                  >
                    IEP {formatScore(loteDetail.iep.score)}
                  </span>
                  <span className="text-sm text-muted-foreground">{loteDetail.lote.linhagem}</span>
                </div>
                {loteDetail.iep.explain.length > 0 && (
                  <ul className="list-disc pl-5 text-sm space-y-1 text-muted-foreground">
                    {loteDetail.iep.explain.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                )}
                <div className="rounded-lg border border-border bg-muted/50 p-4">
                  <p className="text-xs font-medium text-muted-foreground">Métrica suplementar (não compõe o IEP)</p>
                  {loteDetail.metricas_periodo.fcr_estimado_periodo != null ? (
                    <p className="mt-2 text-sm text-foreground">
                      FCR estimado (período):{' '}
                      <span className="font-semibold tabular-nums">
                        {formatFcrEstimado(loteDetail.metricas_periodo.fcr_estimado_periodo)}
                      </span>
                    </p>
                  ) : (
                    <p className="mt-2 text-sm text-muted-foreground">FCR estimado: sem dados suficientes</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    Peso aproximado por categoria de ovo; não substitui pesagem real.
                  </p>
                </div>
                {posturaChartData.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium mb-2">Postura (%): real vs esperado</h4>
                    <div className="h-56 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={posturaChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false} />
                          <XAxis dataKey="semana" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 10 }} width={36} />
                          <Tooltip
                            formatter={(value: number, name: string) => [
                              `${formatScore(value)}%`,
                              name === 'real' ? 'Real' : 'Esperado',
                            ]}
                            labelFormatter={(label) => `Semana ${label}`}
                          />
                          <Line type="monotone" dataKey="real" name="real" stroke={CHART_REAL_COLOR} strokeWidth={2} dot={false} />
                          <Line
                            type="monotone"
                            dataKey="esperado"
                            name="esperado"
                            stroke={CHART_EXPECTED_COLOR}
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={false}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/dashboard')({
  component: DashboardPage,
})
